import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	createOffer,
	createPreContract,
	fetchOfferList,
	fetchPreContractList,
	cancelPreContract,
	acceptPreContract,
	rejectPreContract,
	fetchOfferDetail,
	fetchLikedContracts,
	updateLikedContracts,
	rejectOffer,
	acceptOffer,
	markOfferAsVisited,
	renegotiateOffer,
} from '../services/api'
import { useToken } from '../utils/auth'
/**
 * 캐디에게 계약 제안을 생성하는 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useCreateOffer = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ token, offerData }) => createOffer(token, offerData),
		onSuccess: (data, variables) => {
			console.log('제안 생성 성공:', data)
			// 제안 목록 쿼리 무효화하여 자동 새로고침
			queryClient.invalidateQueries({
				queryKey: ['offer-list', variables.token],
			})
		},
		onError: (error) => {
			console.error('제안 생성 실패:', error)
		},
	})
}

/**
 * 사전계약 제안을 생성하는 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useCreatePreContract = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ token, preContractData }) => createPreContract(token, preContractData),
		onSuccess: (data, variables) => {
			console.log('사전계약 제안 생성 성공:', data)
			// 사전계약 목록 쿼리 무효화하여 자동 새로고침
			queryClient.invalidateQueries({
				queryKey: ['precontract-list', variables.token],
			})
		},
		onError: (error) => {
			console.error('사전계약 제안 생성 실패:', error)
		},
	})
}

/**
 * 사전계약 목록을 조회하는 훅
 * @param {string} token - JWT 토큰
 * @param {boolean} enabled - 쿼리 활성화 여부 (기본: true)
 * @returns {Object} query 객체 { data, isLoading, isError, error, refetch }
 */
export const usePreContractList = (token, enabled = true) => {
	return useQuery({
		queryKey: ['precontract-list', token],
		queryFn: () => fetchPreContractList(token),
		enabled: !!token && enabled, // token이 있고 enabled가 true일 때만 실행
		retry: 1,
		staleTime: 5 * 60 * 1000, // 5분간 fresh 상태 유지
	})
}

/**
 * 사전계약을 취소하는 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useCancelPreContract = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ token, preContractId }) => cancelPreContract(token, preContractId),
		onSuccess: (data, variables) => {
			console.log('사전계약 취소 성공:', data)
			// 사전계약 목록 쿼리 무효화하여 자동 새로고침
			queryClient.invalidateQueries({
				queryKey: ['precontract-list', variables.token],
			})
		},
		onError: (error) => {
			console.error('사전계약 취소 실패:', error)
		},
	})
}

/**
 * 사전계약을 수락하는 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useAcceptPreContract = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ token, preContractId }) => acceptPreContract(token, preContractId),
		onSuccess: (data, variables) => {
			console.log('사전계약 수락 성공:', data)
			// 사전계약 목록 쿼리 무효화하여 자동 새로고침
			queryClient.invalidateQueries({
				queryKey: ['precontract-list', variables.token],
			})
		},
		onError: (error) => {
			console.error('사전계약 수락 실패:', error)
		},
	})
}

/**
 * 사전계약을 거절하는 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useRejectPreContract = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ token, preContractId }) => rejectPreContract(token, preContractId),
		onSuccess: (data, variables) => {
			console.log('사전계약 거절 성공:', data)
			// 사전계약 목록 쿼리 무효화하여 자동 새로고침
			queryClient.invalidateQueries({
				queryKey: ['precontract-list', variables.token],
			})
		},
		onError: (error) => {
			console.error('사전계약 거절 실패:', error)
		},
	})
}

/**
 * 제안 목록을 조회하는 훅
 * @param {string} token - JWT 토큰
 * @param {boolean} enabled - 쿼리 활성화 여부 (기본: true)
 * @returns {Object} query 객체 { data, isLoading, isError, error, refetch }
 */
export const useOfferList = (token, enabled = true) => {
	return useQuery({
		queryKey: ['offer-list', token],
		queryFn: () => fetchOfferList(token),
		enabled: !!token && enabled, // token이 있고 enabled가 true일 때만 실행
		retry: 1,
		staleTime: 5 * 60 * 1000, // 5분간 fresh 상태 유지
	})
}

/**
 * 제안 상세 정보 조회 React Query hook
 * @param {string} offerId - 제안 ID
 * @param {object} options - React Query 옵션
 * @returns {object} React Query 결과
 */
export const useOfferDetail = (offerId, options = {}) => {
	return useQuery({
		queryKey: ['offerDetail', offerId],
		queryFn: () => fetchOfferDetail(offerId),
		enabled: !!offerId,
		...options,
	})
}

/**
 * 사용자의 관심 제안 목록을 조회하는 훅
 * @param {string} userId - 사용자 ID
 * @param {boolean} enabled - 쿼리 활성화 여부 (기본: true)
 * @returns {Object} query 객체 { data, isLoading, isError, error, refetch }
 */
export const useLikedOffers = (userId, enabled = true) => {
	return useQuery({
		queryKey: ['liked-offers', userId],
		queryFn: () => fetchLikedContracts(userId),
		enabled: !!userId && enabled,
		retry: 1,
		staleTime: 5 * 60 * 1000, // 5분간 fresh 상태 유지
	})
}

/**
 * 사용자의 관심 제안을 업데이트하는 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useUpdateLikedOffers = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ userId, likedOfferIds }) => updateLikedContracts(userId, likedOfferIds),
		onSuccess: (data, variables) => {
			console.log('관심 제안 업데이트 성공:', data)
			// 관심 제안 목록 쿼리 무효화하여 자동 새로고침
			queryClient.invalidateQueries({
				queryKey: ['liked-offers', variables.userId],
			})
		},
		onError: (error) => {
			console.error('관심 제안 업데이트 실패:', error)
		},
	})
}

/**
 * 제안 거절 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useRejectOffer = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ token, offerId }) => rejectOffer(token, offerId),
		onSuccess: (data, variables) => {
			console.log('제안 거절 성공:', data)
			// 제안 상세 정보 캐시 무효화
			queryClient.invalidateQueries({ queryKey: ['offerDetail', data.id] })
			// 제안 목록 캐시 무효화
			queryClient.invalidateQueries({ queryKey: ['offer-list'] })
		},
		onError: (error) => {
			console.error('제안 거절 실패:', error)
		},
	})
}

/**
 * 제안 수락 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useAcceptOffer = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ token, offerId }) => acceptOffer(token, offerId),
		onSuccess: (data, variables) => {
			console.log('제안 수락 성공:', data)
			// 제안 상세/목록 캐시 무효화
			queryClient.invalidateQueries({ queryKey: ['offerDetail', data.id] })
			queryClient.invalidateQueries({ queryKey: ['offer-list'] })
		},
		onError: (error) => {
			console.error('제안 수락 실패:', error)
		},
	})
}

/**
 * 제안 방문 상태 업데이트 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useMarkOfferAsVisited = () => {
	const queryClient = useQueryClient()
	const token = useToken()

	return useMutation({
		mutationFn: (offerId) => markOfferAsVisited(token, offerId),
		onSuccess: (data, variables) => {
			console.log('제안 방문 상태 업데이트 성공:', data)
			// 제안 상세 정보 캐시 무효화
			queryClient.invalidateQueries({ queryKey: ['offerDetail', data.id] })
			// 제안 목록 캐시 무효화
			queryClient.invalidateQueries({ queryKey: ['offer-list'] })
		},
		onError: (error) => {
			console.error('제안 방문 상태 업데이트 실패:', error)
		},
	})
}

/**
 * Offer 재협상을 요청하는 mutation 훅
 * @returns {Object} mutation 객체 { mutate, mutateAsync, isLoading, isError, isSuccess, data, error }
 */
export const useRenegotiateOffer = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ offerId, contractData, token }) =>
			renegotiateOffer(offerId, contractData, token),
		onSuccess: (data, variables) => {
			console.log('Offer 재협상 성공:', data)
			// Offer 상세 정보 쿼리 무효화
			queryClient.invalidateQueries({
				queryKey: ['offerDetail', data.id],
			})
			// Offer 목록 쿼리도 무효화
			queryClient.invalidateQueries({
				queryKey: ['offer-list'],
			})
		},
		onError: (error) => {
			console.error('Offer 재협상 실패:', error)
		},
	})
}
